/**
 * AlertFormInitializer Component
 * 
 * A wrapper component that initializes the alert state before rendering the AlertForm.
 * This ensures that the alert object is properly created and initialized before the
 * AlertForm tries to access its properties.
 * 
 * It also checks for process data in localStorage to support quick alert creation
 * from the process monitoring panel.
 * 
 * It also ensures that all necessary CSS styles are properly loaded and applied.
 */

import React, { useEffect, useState, useRef } from 'react';
import { useAlertContext } from '../../contexts/AlertContext';
import AlertForm from './AlertForm';
import AlertFormStyleLoader from './AlertFormStyleLoader';
import { AlertType, createAlertRule } from '../../models/AlertModels';

/**
 * AlertFormInitializer component
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.hideTitle - Whether to hide the title (useful when displayed in a Modal)
 * @returns {JSX.Element} The AlertForm component with initialized state
 */
function AlertFormInitializer({ hideTitle = true }) {
  const { startNewAlert, formState, updateFormState } = useAlertContext();
  const [isInitializing, setIsInitializing] = useState(false);
  const hasInitialized = useRef(false);
  const previousAlertRef = useRef(formState.alert);
  
  // Detect when alert is reset (e.g., after cancellation) and reset initialization flags
  useEffect(() => {
    // If we had an alert before but now it's null, the form was reset (e.g., cancelled)
    if (previousAlertRef.current && !formState.alert) {
      // Reset initialization flags to allow re-initialization
      hasInitialized.current = false;
      setIsInitializing(false);
    }
    
    // Update the previous alert reference
    previousAlertRef.current = formState.alert;
  }, [formState.alert]);
  
  // Initialize the alert state when the component mounts or after reset
  useEffect(() => {
    // Prevent multiple initializations
    if (hasInitialized.current) {
      // Skip initialization if already done
      return;
    }
    
    // Only initialize if alert is null to avoid overwriting existing state
    if (!formState.alert && !isInitializing) {
      // Initialize the alert form
      setIsInitializing(true);
      hasInitialized.current = true;
      
      try {
        // Check if there's process data for quick alert creation
        const storedProcessData = localStorage.getItem('quickAlertProcess');

        
        if (storedProcessData) {
          // Process-initiated alert flow
          initializeProcessAlert(storedProcessData);
        } else {
          // Default alert flow
  
          initializeDefaultAlert();
        }
        
        // Clear any stored process data to prevent future initializations
        localStorage.removeItem('quickAlertProcess');
      } catch (error) {
        console.error('[AlertFormInitializer] Error initializing alert form:', error);
        // Fallback to default alert type in case of error
        initializeDefaultAlert();
      } finally {
        setIsInitializing(false);
      }
    }
  }, [formState.alert, isInitializing]);
  
  /**
   * Initialize a process-targeted alert
   * @param {string} storedProcessData - JSON string of process data
   */
  const initializeProcessAlert = (storedProcessData) => {
    try {
      const processData = JSON.parse(storedProcessData);

      if (!processData || !processData.processName) {
        // Process data is invalid, falling back to default alert
        initializeDefaultAlert();
        return;
      }
      
      // Create a default CPU usage rule with process targeting enabled
      const rule = createAlertRule({
        event: 'cpu_usage', 
        condition: 'gt',
        value: '80',
        processNameTargetingEnabled: true,
        processNamePattern: processData.processName
      });
      
      // Create a pre-configured alert with the process data
      const alertData = {
        type: AlertType.PROCESS,
        rules: [rule],
        name: `Alert for ${processData.processName}`,
        description: `Monitors resource usage for process: ${processData.processName}`,
        // Set flags at both root level and in meta for maximum compatibility
        isProcessInitiated: true,
        targetProcessName: processData.processName,
        meta: {
          initiatedFromProcess: true,
          lockAlertType: true,
          lockProcessName: true,
          targetProcessName: processData.processName,
          createdAt: new Date().toISOString()
        }
      };
      

      
      // Start a new alert with the process data
      startNewAlert(AlertType.PROCESS, alertData);

    } catch (error) {
      console.error('[AlertFormInitializer] Error initializing process alert:', error);
      initializeDefaultAlert();
    }
  };
  
  /**
   * Initialize a default global alert
   */
  const initializeDefaultAlert = () => {
    startNewAlert(AlertType.GLOBAL);
    console.log('[AlertFormInitializer] Called startNewAlert for default (GLOBAL) alert.');
  };
  
  // Show a loading state until the alert is initialized
  if (!formState.alert) {
    return (
      <AlertFormStyleLoader>
        <div className="alert-form-loading">Initializing form...</div>
      </AlertFormStyleLoader>
    );
  }
  

  
  // Render the AlertForm once the alert state is initialized with proper styling
  return (
    <AlertFormStyleLoader>
      <AlertForm hideTitle={hideTitle} />
    </AlertFormStyleLoader>
  );
}

export default AlertFormInitializer;
